import axios, { AxiosError, AxiosResponse } from "axios";
import { toast } from "react-toastify";
import { PaginatedResult, PagingParams } from "../models/pagination";
import {
  User,
  UserFormValues,
  VendorScoreForm,
  VendorsResponse,
  VendorRegistrationForm,
  PasswordChangeForm,
  PasswordOtpData,
  VendorUpdateForm,
  INotificationResponse,
  IUserRolesResponse,
  IUserRoleForm,
  IVendorUsersResponse,
  IUserRoleResponse,
  UpdateUserRole,
  IVendorsAccountResponse,
  UserProfile,
} from "../models/user";
import { router } from "../router/Routes";
import { store } from "../stores/store";
import {
  IProductBrandResponse,
  IProductInverterTypesResponse,
  IProductResponse,
  IProductsResponse,
  ProductCategoryResponse,
  ProductForm,
  UpdateProductVisibility,
  UpdateProductForm,
} from "../models/product";
import { IOrderItemUpdate, IOrderResponse } from "../models/orders";
import {
  IImalURlResponse,
  ITicketResponse,
  TicketForm,
} from "../models/tickets";
import {
  IReturnLocation,
  IReturnLocationResponse,
  IReturnLocationsResponse,
  IShippingPriceResponse,
  ShippingPriceForm,
} from "../models/shipping";
import PBKDF2 from "crypto-js/pbkdf2";
import Utf8 from "crypto-js/enc-utf8";
import AES from "crypto-js/aes";
import { IVendorResponse, IVendorsResponse } from "../models/general";
import { ITermsResponse, TermsForm } from "../models/terms";

axios.defaults.baseURL = process.env.REACT_APP_API_URL;
const PassPhrase = String(process.env.REACT_APP_PassPhrase);
const SaltValue = String(process.env.REACT_APP_SaltValue);
const InitVector = String(process.env.REACT_APP_InitVector);
const PasswordIterations = Number.parseInt(
  String(process.env.REACT_APP_PasswordIterations)
);
const Blocksize = Number.parseInt(String(process.env.REACT_APP_Blocksize));

// Encryption Service
const encryptRequest = (request: any) => {
  var key = PBKDF2(PassPhrase, Utf8.parse(SaltValue), {
    keySize: 256 / Blocksize,
    iterations: PasswordIterations,
  });
  var parsedIV = Utf8.parse(InitVector);

  // Encrypt
  var encrypted = AES.encrypt(JSON.stringify(request), key, {
    iv: parsedIV,
    // mode: CryptoJS.mode.CFB,
    // padding: CryptoJS.pad.AnsiX923
  }).toString();
  // console.log(request);
  // console.log(encrypted);
  return encrypted;
};

// Decryption Service
const decryptResponse = (response: any) => {
  var key = PBKDF2(PassPhrase, Utf8.parse(SaltValue), {
    keySize: 256 / Blocksize,
    iterations: PasswordIterations,
  });
  var parsedIV = Utf8.parse(InitVector);

  // Decrypt
  var bytes = AES.decrypt(response, key, { iv: parsedIV });
  var decryptedResponse = bytes.toString(Utf8);
  // console.log(JSON.parse(decryptedResponse));

  return JSON.parse(decryptedResponse);
};


axios.interceptors.request.use((config) => {
  const token = store.commonStore.GetItem("jwt");
  config.headers!["x-clientid"] = process.env.REACT_APP_ClientId;
  if (token && config.headers) config.headers.Authorization = `Bearer ${token}`;
  config.transformRequest = [
    (data, headers) => {
      return data;
    },
  ];
  return config;
});

let isRefreshing = false;  // Flag to prevent multiple refresh token requests
let refreshSubscribers = [] as Array<any>;

function subscribeTokenRefresh(cb: any) {
  refreshSubscribers.push(cb);
}

function onTokenRefreshed(token: any) {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
}

axios.interceptors.response.use(
  async (response) => {
    // Handle pagination if present
    const pagination = response.headers["pagination"];
    if (pagination) {
      response.data = new PaginatedResult(
        response.data,
        JSON.parse(pagination)
      );
      return response as AxiosResponse<PaginatedResult<any>>;
    }
    return response;
  },
  async (error) => {
    try {
      // console.log(error);
      const { status } = error.response || {};
      const { data } = error.response || {}

      // Handle different error status codes
      switch (status) {
        case 400:
          toast.error("Invalid Request");
          // console.log(error.response)
          break;
        case 401:

          let decryptedResponse = null
          try {
            decryptedResponse = decryptResponse(data) as any;
          } catch (e) {
            decryptedResponse = null
            // console.log("Decryption failed or data was malformed for 401 response:", e);
          }

          if (decryptedResponse && (decryptedResponse.ResponseCode == "401" && decryptedResponse.ResponseMessage == "Session is invalid")) {
            window.localStorage.removeItem("jwt");
            window.localStorage.removeItem("refjwt");
            window.localStorage.removeItem("user");
            store.commonStore.setToken(null);
            router.navigate("/");

            toast.info("Your session is not active. Please log in again.");
          } else {

            if (!isRefreshing) {
              isRefreshing = true;

              // Make the refresh token request
              try {
                // const refreshToken = window.localStorage.getItem("refreshToken");
                const loggedInUser = store.commonStore.loggedInUser as UserProfile
                const token = store.commonStore.reftoken
                const payload = {
                  refreshToken: token,
                  userId: loggedInUser.userId,
                  email: loggedInUser.email
                };
                const response = await requests.post("/Vendor/refreshtokenforvendor", payload);
                  // console.log(response.data);
                const newToken = response.data.accessToken;
                const newrefToken = response.data.refreshToken;
                window.localStorage.setItem(
                  "jwt",
                  agent.encryptRequest(newToken)
                );  // Store the new access token
                window.localStorage.setItem(
                  "refjwt",
                  agent.encryptRequest(newrefToken)
                );  // Store the new refresh token
                store.commonStore.setToken(newToken);  // Update token in your state
                store.commonStore.setRefToken(newrefToken);  // Update refresh token in your state

                // Notify all subscribers (waiting requests)
                onTokenRefreshed(newToken);

                // Retry the original request with the new token
                return axios(error.config);
              } catch (refreshError) {
                // If refresh token fails, clear user data and redirect to login
                window.localStorage.removeItem("jwt");
                window.localStorage.removeItem("refjwt");
                window.localStorage.removeItem("user");
                store.commonStore.setToken(null);
                router.navigate("/");

                toast.info("Your session has expired, please login again");
              } finally {
                isRefreshing = false;
              }
            }

          }


          // If the error is a 401, try to refresh the token


          // If token is being refreshed, queue the request and resolve once the token is refreshed
          return new Promise((resolve) => {
            subscribeTokenRefresh((newToken: any) => {
              error.config.headers["Authorization"] = `Bearer ${newToken}`;
              resolve(axios(error.config));
            });
          });

        case 403:
          toast.error("Forbidden");
          break;
        case 404:
          router.navigate("/not-found");
          break;
        case 500:
          toast.error("Service Downtime");
          break;
        default:
          toast.error("An unexpected error occurred");
          // console.log(error.response)
          break;
      }

      return Promise.reject(error);
    } catch (error) {
      // console.log(`${error}`);
      if (navigator.onLine === false)
        toast.warning("Please check your internet connection");
      else toast.error("Service Downtime");
      return Promise.reject(error);
    }
  }
);
const responseBody = <T>(response: AxiosResponse<T>) =>
  decryptResponse(response.data);
const requests = {
  get: <T>(url: string) => axios.get<T>(url).then(responseBody),
  post: <T>(url: string, body: {}) =>
    axios
      .post<T>(url, encryptRequest(body), {
        headers: { "Content-type": "application/json;" },
      })
      .then(responseBody),
  postUrl: <T>(url: string, body: any) =>
    axios
      .post<T>(url, encryptRequest(body), {
        headers: { "Content-type": "application/json;" },
      })
      .then(responseBody),
  postWithoutBody: <T>(url: string) => axios.post<T>(url).then(responseBody),
  put: <T>(url: string, body: {}) =>
    axios
      .put<T>(url, encryptRequest(body), {
        headers: { "Content-type": "application/json;" },
      })
      .then(responseBody),
  putWithoutBody: <T>(url: string) => axios.put<T>(url).then(responseBody),
  del: <T>(url: string) => axios.delete<T>(url).then(responseBody),
  patch: <T>(url: string) => axios.patch<T>(url).then(responseBody),
  patchWithBody: <T>(url: string, body: {}) =>
    axios
      .patch<T>(url, encryptRequest(body), {
        headers: { "Content-type": "application/json;" },
      })
      .then(responseBody),
};

const Activities = {
  getVendorStore: (id: string) =>
    requests.get<IVendorResponse>(`/Vendor/getvendorbyslug?slug=${id}`),
  getVendorStores: () =>
    requests.get<IVendorsResponse>(`/Vendor/getallvendors`),
};

const Account = {
  current: () => requests.get<User>("account"),
  login: (user: UserFormValues) =>
    requests.post<VendorsResponse>(`/Vendor/login`, user),
  register: (user: VendorRegistrationForm) =>
    requests.post<VendorsResponse>("/Vendor/register", user),
  updateAccountInfo: (user: VendorUpdateForm) =>
    requests.put<VendorsResponse>(
      "/Vendor/updateVendorAccountInformation",
      user
    ),
  forgotPassword: (user: string) =>
    requests.postWithoutBody<VendorsResponse>(
      `/Vendor/forgotpasswordforvendor?email=${user}`
    ),
  refreshToken: (payload: any) =>
    requests.post<VendorsResponse>(`/Vendor/refreshtokenforvendor`, payload),
  verifyPasswordOtp: (data: PasswordOtpData) =>
    requests.post<VendorsResponse>("/OTP/validateuserotp", data),
  changePassword: (user: PasswordChangeForm) =>
    requests.post<VendorsResponse>("/Vendor/resetpasswordforvendor", user),
  verifyEmail: (user: { email: string }) =>
    requests.post<VendorsResponse>(`/Vendor/verifyvendor`, user),
  getEligibility: (values: VendorScoreForm) =>
    requests.post<VendorsResponse>("/Vendor/vendoreligibilitystatus", values),
  getAccountInfo: () =>
    requests.get<VendorsResponse>("/Vendor/getvendoraccountinformation"),
  getCategories: (params: PagingParams) =>
    requests.get<VendorsResponse>(
      `/Category/getallcategories?PageNumber=${params.pageNumber
      }&PageSize=${100}`
    ),
  getStateCodes: () => requests.get<VendorsResponse>(`/Imal/getstatecodes`),
  verifyAccountNumber: (account: string) =>
    requests.get<IVendorsAccountResponse>(
      `/Imal/getaccountdetailsbynuban?AccountNumber=${account}`
    ),
  logoutUser: () => requests.postWithoutBody<VendorsResponse>('Vendor/vendorlogout')
};

const Profiles = {
  getVendorMatrix: () => requests.get<any>(`/Vendor/getvendormatrix`),
  getVendorNotifications: (date: string) =>
    requests.get<INotificationResponse>(
      `/Vendor/getvendornotification?searchDate=${date}`
    ),
  getVendorRoles: () =>
    requests.get<IUserRolesResponse>(`/Vendor/getallvendorroles`),
  createVendorSubAdmin: (user: IUserRoleForm) =>
    requests.post<VendorsResponse>("/Vendor/registeruserstore", user),
  getUserRole: (id: string) =>
    requests.get<IUserRoleResponse>(`/Vendor/getuserstore?storeUserId=${id}`),
  updateUserRole: (payload: UpdateUserRole) =>
    requests.patchWithBody<IUserRoleResponse>(
      `/Vendor/updateuserstore`,
      payload
    ),
  getVendorUsers: (searchTerm: string) => {
    const searchParam = searchTerm ? `searchword=${searchTerm}` : '';
    return requests.get<IVendorUsersResponse>(
      `/Vendor/getalluserstores${searchParam ? `?${searchParam}` : ''}`
    );
  }
  ,
  getVendorUsersWithPagination: (params: PagingParams) =>
    requests.get<IVendorUsersResponse>(
      `/ProductBrand/getalluserstores?PageNumber=${params.pageNumber}&PageSize=${params.pageSize}`
    ),
};
const Product = {
  getCategories: (params: PagingParams) =>
    requests.get<VendorsResponse>(
      `/Category/getallcategories?PageNumber=${params.pageNumber
      }&PageSize=${100}`
    ),
  getBrands: (params: PagingParams) =>
    requests.get<IProductBrandResponse>(
      `/ProductBrand/getallproductbrands?PageNumber=${params.pageNumber
      }&PageSize=${100}`
    ),
  getInverterTypes: () =>
    requests.get<IProductInverterTypesResponse>(`/Product/invbatnames`),
  getBrandsWithPagination: (params: PagingParams) =>
    requests.get<IProductBrandResponse>(
      `/ProductBrand/getallproductbrands?PageNumber=${params.pageNumber}&PageSize=${params.pageSize}`
    ),
  addProduct: (payload: ProductForm) =>
    requests.post<VendorsResponse>(`/Product/createProduct`, payload),
  addMultipleProducts: (payload: ProductForm[]) =>
    requests.post<VendorsResponse>(`/Product/CreateMultipleProducts`, payload),
  updateProduct: (payload: UpdateProductForm) =>
    requests.put<VendorsResponse>(`/Product/updateProduct`, payload),
  updateProductVisibility: (payload: UpdateProductVisibility) =>
    requests.patchWithBody<VendorsResponse>(
      `/Product/updateproductenable`,
      payload
    ),
  removeProduct: (payload: string) =>
    requests.put<VendorsResponse>(
      `/Product/RemoveProduct?productId=${payload}`,
      {}
    ),
  getVendorProducts: (searchTerm: string) => {
    const searchParam = searchTerm ? `searchword=${searchTerm}` : '';
    return requests.get<IProductsResponse>(`/Product/getAllVendorProducts?${searchParam}`);
  },
  getProduct: (id: string) =>
    requests.get<IProductResponse>(`/Product/getVendorProduct?productId=${id}`),
};

const Orders = {
  getVendorOrdersWithPagination: (params: PagingParams) =>
    requests.get<IProductBrandResponse>(
      `/OrderItem/getvendorallorderitems=${params.pageNumber}&PageSize=${params.pageSize}`
    ),
  updateOrder: (payload: IOrderItemUpdate) =>
    requests.put<VendorsResponse>(
      `/OrderItem/updateproducttoorderitem`,
      payload
    ),
  updateMultipleOrders: (payload: IOrderItemUpdate[]) =>
    requests.put<VendorsResponse>(
      `/OrderItem/updatemultipleproducttoorderitem`,
      payload
    ),
  getVendorOrders: (vendorId: string, searchTerm: string) => {
    const searchParam = searchTerm ? `searchword=${searchTerm}` : '';
    return requests.get<IOrderResponse>(
      `/OrderItem/getvendorallorderitems?VendorId=${vendorId}${searchParam ? `&${searchParam}` : ''}`
    );
  },
  getCallBackResponse: (request: string) =>
    requests.get<any>(
      `/CardPayment/paymentStatus?encryptedResponse=${request}`
    ),
};

const Tickets = {
  getVendorTicketsWithPagination: (params: PagingParams) =>
    requests.get<ITicketResponse>(
      `/Vendor/getallvendortickets=${params.pageNumber}&PageSize=${params.pageSize}`
    ),
  createTicket: (payload: TicketForm) =>
    requests.post<ITicketResponse>(`/Vendor/createticket`, payload),
  getVendorTickets: (searchTerm: string) => {
    const searchParam = searchTerm ? `searchword=${searchTerm}` : '';
    return requests.get<ITicketResponse>(
      `/Vendor/getallvendortickets${searchParam ? `?${searchParam}` : ''}`
    );
  }
  ,
  createUrl: (payload: string) => {
    return requests.post<IImalURlResponse>(`/Imal/NewUploadFile`, {
      postedFilebase64: payload,
    });
  },
};

const Shipping = {
  getVendorReturnLocationsWithPagination: (params: PagingParams) =>
    requests.get<IReturnLocationsResponse>(
      `/Vendor/getallreturnlocations=${params.pageNumber}&PageSize=${params.pageSize}`
    ),
  createReturnLocations: (payload: IReturnLocation[]) =>
    requests.post<IReturnLocationsResponse>(
      `/Vendor/createreturnproductlocation`,
      payload
    ),
  updateReturnLocation: (payload: IReturnLocation) =>
    requests.put<IReturnLocationResponse>(
      `/Vendor/updatereturnproductlocation`,
      payload
    ),
  removeReturnLocation: (payload: string) =>
    requests.patch<VendorsResponse>(
      `/Vendor/removereturnproductlocation?ReturnProductLocationId=${payload}`
    ),
  createShippingRegionPriceList: (payload: ShippingPriceForm[]) =>
    requests.post<IReturnLocationsResponse>(
      `/Vendor/createshippingprice`,
      payload
    ),
  createShippingStatePriceList: (payload: ShippingPriceForm[]) =>
    requests.post<IReturnLocationsResponse>(
      `/Vendor/createshippingprice`,
      payload
    ),
  getVendorReturnLocation: (id: string) =>
    requests.get<IReturnLocationResponse>(
      `/Vendor/getReturnproductlocation?ReturnProductLocationId=${id}`
    ),
  getVendorReturnLocations: () =>
    requests.get<IReturnLocationsResponse>(
      `/Vendor/getallreturnproductlocations`
    ),
  getVendorShippingPriceLocations: () =>
    requests.get<IShippingPriceResponse>(`/Vendor/getshippingprice`),
  getVendorStateLocations: () =>
    requests.get<IReturnLocationsResponse>(`/Vendor/getallstatelocations`),
};

const Terms = {
  downloadfile: (payload: TermsForm) =>
    requests.post<ITermsResponse>(`/Imal/downloadfile`, payload),
  createUrl: (payload: string) => {
    return requests.post<IImalURlResponse>(`/Imal/NewUploadFile`, {
      postedFilebase64: payload,
    });
  },
  getCAC: () => requests.get<VendorsResponse>("/Vendor/getvendormoulink"),
  updateCac: (payload: string) =>
    requests.putWithoutBody<ITermsResponse>(
      `/Vendor/updatevendormou?request=${payload}`
    ),
};
const agent = {
  Activities,
  Account,
  Profiles,
  Product,
  Orders,
  Tickets,
  Shipping,
  Terms,
  encryptRequest,
  decryptResponse,
};

export default agent;
